import * as React from "react";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Keyword alerts — per-user regex patterns that fire an SSE ping when a
 * matching message is posted anywhere the user can read.
 */
export default function KeywordAlertsDialog({ open, onOpenChange, companyId }) {
  const [patterns, setPatterns] = React.useState([]);
  const [draft, setDraft] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!open) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/comms/keyword-alerts`);
      setPatterns(data.patterns || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load alerts");
    }
  }, [companyId, open]);

  React.useEffect(() => { load(); }, [load]);

  async function save(next) {
    setBusy(true);
    try {
      await api.put(`/companies/${companyId}/comms/keyword-alerts`, { patterns: next });
      setPatterns(next);
      toast.success("Alerts updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  }

  function addOne() {
    const p = draft.trim();
    if (!p) return;
    save([...patterns, p]);
    setDraft("");
  }
  function removeAt(i) {
    save(patterns.filter((_, idx) => idx !== i));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="keyword-alerts-dialog">
        <DialogHeader>
          <DialogTitle>Keyword alerts</DialogTitle>
          <DialogDescription>
            Get a live ping when a message matches one of these patterns.
            Regex-friendly (case-insensitive).
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addOne()}
            placeholder="e.g. tap water, walkout, closing early"
            className="field-input h-9 flex-1 text-[13px]"
            data-testid="keyword-alerts-input"
          />
          <button
            type="button"
            onClick={addOne}
            disabled={busy || !draft.trim()}
            className="btn-violet h-9"
            data-testid="keyword-alerts-add"
          >
            <Plus size={13} /> Add
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="keyword-alerts-list">
          {patterns.length === 0 && (
            <div className="text-[12.5px] text-brand-muted">No alerts yet.</div>
          )}
          {patterns.map((p, i) => (
            <span
              key={`${p}-${i}`}
              className="chip mono-num text-[11px] chip-violet flex items-center gap-1"
              data-testid={`keyword-alerts-chip-${i}`}
            >
              {p}
              <button
                type="button"
                onClick={() => removeAt(i)}
                disabled={busy}
                className="hover:text-brand-coral"
                aria-label="Remove alert"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
